<template>
  <div>

    <create-user-popup
      v-if="showAddModal"
      @close="showAddModal = false"
      @submitted="refreshData(); showAddModal = false;"
    />

    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2 mb-1">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="2"
            class="mb-50 mb-md-0"
          >
            <b-form-select
              :value="query.limit"
              :options="limitOptions"
              @input="limitChanged"
            />
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-50 mb-md-0"
          >
            <b-form-select
              id="userStatusFilter"
              :value="query.status"
              :options="statusFilterOptions"
              @input="statusChanged"
            />
          </b-col>
          <b-col
            cols="12"
            md="5"
            class="mb-50 mb-md-0"
          >
            <b-form-input
              :value="query.query"
              type="search"
              :placeholder="$t('shared.search')"
              @keyup.enter.native="queryChanged($event.target.value)"
              @blur="queryChanged($event.target.value)"
              @input="onSearchInput"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="text-md-right"
          >
            <b-button
              v-if="canIEdit"
              variant="primary"
              class="btn-block"
              @click="showAddModal = true"
            >
              {{ $t('shared.button.add') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading">
        <b-table
          ref="refUserListTable"
          class="position-relative"
          :items="users"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          :empty-text="loading ? $t('shared.loading') : $t('shared.no-data') "
          :sort-by.sync="query.sortBy"
          :sort-desc.sync="query.isSortDirDesc"
        >

          <!-- Column: surname -->
          <template #cell(surname)="data">

            <b-link
              v-if="canIEdit"
              class="text-nowrap d-block"
              :to="{ name: 'user-edit', params: { id: data.item.id } }"
            >
              {{ data.item.surname }} {{ data.item.name }}
            </b-link>
            <span v-else>
              {{ data.item.surname }} {{ data.item.name }}
            </span>
            <small class="text-muted d-block">
              {{ data.item.position || '-' }}
            </small>

          </template>

          <template #cell(username)="data">
            <span class="text-nowrap">{{ data.item.username || '-' }}</span>
          </template>

          <template #cell(phoneNumber)="data">
            <b-link
              v-if="data.item.phoneNumber"
              :href="`tel:${data.item.phoneNumber}`"
              class="text-nowrap"
            >
              {{ data.item.phoneNumber }}
            </b-link>
            <span v-else>-</span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="data.item.status ? 'light-success' : 'light-secondary'">
              {{ data.item.status ? $t('conf.user.active') : $t('conf.user.inactive') }}
            </b-badge>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">

            <div class="text-nowrap">
              <feather-icon
                v-if="canIEdit"
                icon="EditIcon"
                class="cursor-pointer"
                size="16"
                @click="editUser(data.item.id)"
              />
            </div>

          </template>

        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCol, BFormInput, BFormSelect, BLink, BRow, BTable,
} from 'bootstrap-vue'
import CreateUserPopup from '@/views/users/popups/CreateUserPopup.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import guard from '@/guard'

export default {
  components: {
    TableBottom,
    CreateUserPopup,
    RcOverlay,

    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BFormSelect,
    BTable,
    BLink,
    BRow,
  },
  data() {
    return {
      showAddModal: false,
      loading: false,

      total: 0,
      query: {
        sortBy: null,
        isSortDirDesc: false,
        query: null,
        status: 'active',
        limit: 10,
        page: 1,
      },

      tableColumns: [
        {
          label: this.$t('conf.user.surname-name'),
          key: 'surname',
          sortable: true,
        },
        {
          label: this.$t('conf.user.popup.phone_input'),
          key: 'phoneNumber',
          sortable: false,
        },
        {
          label: this.$t('shared.username'),
          key: 'username',
          sortable: true,
        },
        {
          label: this.$t('conf.user.status'),
          key: 'status',
          sortable: true,
        },
        {
          label: this.$t('shared.actions'),
          key: 'actions',
          value: null,
          class: 'actions-column',
        },
      ],
    }
  },
  computed: {
    limitOptions() {
      return [5, 10, 25, 50, 100].map(value => ({ value, text: String(value) }))
    },
    users() {
      return this.$store.getters['user/getUsers']
    },
    statusFilterOptions() {
      return [
        { value: 'all', text: this.$t('conf.user.status_all') },
        { value: 'active', text: this.$t('conf.user.active') },
        { value: 'inactive', text: this.$t('conf.user.inactive') },
      ]
    },
    canIEdit() {
      return guard.hasAtLeastOneRole(this.$store.state.auth.activeUser.roles, ['ROLE_USER_MANAGEMENT'])
    },
  },
  watch: {
    query: {
      handler() {
        this.refreshData()
      },
      deep: true,
    },
  },
  created() {
    this.refreshData()
  },
  methods: {
    refreshData() {
      this.loading = true
      const query = {
        q: this.query.query,
        sortField: this.query.sortBy,
        sortDir: this.query.isSortDirDesc ? 'desc' : 'asc',
        status: this.query.status,
        limit: this.query.limit,
        page: this.query.page,
      }

      Promise.all([this.$store.dispatch('user/fetchUsers', query)])
        .then(res => {
          if (this.query.page > 1 && res[0].items.length === 0) {
            this.query.page = 1
          }
          this.total = res[0].total
          this.loading = false
        })
      // eslint-disable-next-line no-console
        .catch(err => console.log(err))
    },
    deleteUser(id) {
      return id
    },
    editUser(id) {
      this.$router.push(`/user/edit/${id}`)
    },
    limitChanged(newLimit) {
      this.query.limit = Number(newLimit)
      this.query.page = 1
    },
    queryChanged(newQuery) {
      this.query.query = newQuery
      this.query.page = 1
    },
    statusChanged(newStatus) {
      this.query.status = newStatus
      this.query.page = 1
    },
    onSearchInput(value) {
      if ((value || '').length === 0) {
        this.queryChanged('')
      }
    },
  },
}
</script>

<style lang="scss">

.actions-column {
  width: 1rem;
}
</style>
